import type { TemplateVariable } from '../../interfaces/template-variable.interface';
import { templateEditorLogger } from '../../logger.instance';
import { CLASSES, SELECTORS } from '../../utils/constants';
import type {
  Expression,
  ContentStatement,
  MustacheStatement,
  SubExpression,
  Statement,
  Program,
} from '../ast-node.model';
import { PathExpressionNode } from '../path-expression';
import { LiteralParser } from './literal-parser';

const logger = templateEditorLogger.extend('argument-parser');

/**
 * Utility for parsing arguments from DOM elements.
 */
export class ArgumentParser {
  private static IS_PATH_LOOKALIKE =
    /(?:@key)|(?:@index)|(?:(?:context|input|dangerous|custom|this|@root)\.\w)/;

  /**
   * Parses arguments from a DOM element's children.
   */
  public static parseArguments(
    element: HTMLElement,
    astBuilder: (node: Node) => Program | Statement[],
    knownVariables: Map<string, TemplateVariable>,
  ): Expression[] {
    const params: Expression[] = [];
    logger.debug('Starting parseArguments', { element });

    for (const child of Array.from(element.children)) {
      if (!child.matches(`.${CLASSES.ARG_WRAPPER}`)) continue;

      const argSpan = child.querySelector<HTMLElement>(`span${SELECTORS.ARG_NAME}`);
      if (!argSpan || argSpan.classList.contains(CLASSES.IS_PLACEHOLDER)) continue;

      const param = this.parseArgument(argSpan, astBuilder, knownVariables);
      if (param) params.push(param);
    }

    logger.debug('Finished parseArguments', { params });
    return params;
  }

  /**
   * Parses a single argument from a DOM element.
   */
  private static parseArgument(
    argSpan: HTMLElement,
    astBuilder: (node: Node) => Program | Statement[],
    knownVariables: Map<string, TemplateVariable>,
  ): Expression | null {
    const builtAst = astBuilder(argSpan);
    logger.debug('parseArgument', { argSpan, builtAst });

    const argAst = Array.isArray(builtAst) ? builtAst : builtAst.body;
    const mustacheNode = argAst.find(
      (node): node is MustacheStatement => node.type === 'MustacheStatement',
    );

    // Argument is a chip (variable or function)
    if (mustacheNode) {
      const isFunction =
        mustacheNode.params.length > 0 ||
        knownVariables.get(mustacheNode.path.original)?.type === 'function';

      const result = isFunction
        ? ({
            type: 'SubExpression',
            path: mustacheNode.path,
            params: mustacheNode.params,
            toString: () =>
              `(${[mustacheNode.path.toString(), ...mustacheNode.params.map((param) => param.toString())].join(' ')})`,
          } as SubExpression)
        : mustacheNode.path;

      logger.debug('Parsed as chip', { mustacheNode, isFunction, result });
      return result;
    }

    // Argument is plain text
    const content = argAst
      .filter((node): node is ContentStatement => node.type === 'ContentStatement')
      .map((node) => node.value)
      .join('')
      .trim();

    if (content) {
      const isQuoted =
        (content.startsWith('"') && content.endsWith('"')) ||
        (content.startsWith("'") && content.endsWith("'"));

      const isPathLookalike = ArgumentParser.IS_PATH_LOOKALIKE.test(content);

      let result: Expression;

      if (isQuoted) {
        result = LiteralParser.parse(content);
      } else if (knownVariables.has(content)) {
        result = new PathExpressionNode(content);
      } else if (LiteralParser.isLiteral(content)) {
        result = LiteralParser.parse(content);
      } else if (isPathLookalike) {
        result = new PathExpressionNode(content);
      } else {
        result = LiteralParser.parse(content);
      }

      logger.debug('Parsed as text', { content, isQuoted, isPathLookalike, result });
      return result;
    }

    logger.debug('Parsed as null');
    return null;
  }
}
